// Sub-menu for the Dashboards row in the Insights panel (operator
// request, 23 Oct): quick access to every dashboard without opening
// the gallery first, grouped as Pinned, Across your apps, By app
// (each app's overview pages, in app order) and Your KPI dashboards,
// then "All dashboards". App overviews are included even though they
// also sit in their own app's menu: this menu is the one place to
// reach any dashboard.
//
// Visibility uses the gallery's own rule (canSeeDashboard), so the menu
// never lists a dashboard the person couldn't open. AppLayout remounts
// on every page, so pins and the KPI list are cached briefly per
// user and org instead of being re-fetched on each navigation.

import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import type { AppSidebarItem } from './apps';
import { getDashboardPins } from './dashboardPins';
import { DASHBOARDS } from './dashboardRegistry';
import {
  APP_LABELS,
  APP_ORDER,
  PINNED_GROUP,
  canSeeDashboard,
  kpiDashboardEntries,
  type DashboardAccess,
  type DashboardEntry,
} from './dashboards';
import { useOrgEntitlements } from './entitlements';
import { listKpiDashboards } from './kpis';
import { useAllFeatureFlags } from './useFeatureFlag';

const TTL_MS = 60_000;
interface Cached {
  at: number;
  pins: string[];
  kpi: DashboardEntry[];
}
const cache = new Map<string, Cached>();
const listeners = new Set<() => void>();

/** Call after pins or KPI dashboards change so open menus refresh. */
export function invalidateDashboardMenu(): void {
  cache.clear();
  for (const fn of listeners) fn();
}

// Lucide names used by dashboard entries → the shell's sidebar icon keys.
const ICON_KEY: Record<string, string> = {
  gauge: 'gauge',
  'clipboard-check': 'clipboard-list',
  users: 'users',
  radar: 'radar',
  'layout-grid': 'layout-grid',
  activity: 'activity',
  archive: 'archive',
  'file-text': 'file-text',
  globe: 'globe',
  'graduation-cap': 'graduation-cap',
  home: 'home',
  laptop: 'laptop',
  mail: 'mail',
  'shield-check': 'shield-check',
  'shield-alert': 'shield-alert',
  sparkles: 'sparkles',
};

const toItem =
  (group: string) =>
  (e: DashboardEntry): AppSidebarItem => ({
    to: e.route,
    label: e.title,
    iconKey: ICON_KEY[e.icon] ?? 'dashboard',
    end: true,
    group,
    shortcut: true,
  });

// Org-wide pages that already have their own Insights rows.
const OWN_ROW = new Set(['reports', 'kpis']);

/** Children for the Dashboards row, or null until access is known. */
export function useDashboardMenu(): AppSidebarItem[] | null {
  const { session } = useAuth();
  const orgId = session?.activeOrgId ?? null;
  const uid = session?.user.uid ?? null;
  const isAdmin = !!session?.orgRoles && (session.orgRoles.includes('owner') || session.orgRoles.includes('admin'));
  const { data: ent } = useOrgEntitlements();
  const { flags } = useAllFeatureFlags();
  const cacheKey = uid && orgId ? `${uid}:${orgId}` : null;
  const [data, setData] = useState<Cached | null>(() => (cacheKey ? cache.get(cacheKey) ?? null : null));
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const fn = () => setTick((n) => n + 1);
    listeners.add(fn);
    return () => {
      listeners.delete(fn);
    };
  }, []);

  useEffect(() => {
    if (!session || !orgId || !cacheKey) return;
    const hit = cache.get(cacheKey);
    if (hit && Date.now() - hit.at < TTL_MS) {
      setData(hit);
      return;
    }
    let cancelled = false;
    void (async () => {
      const idToken = await session.user.getIdToken();
      // Each source fails on its own: no pins or no KPI list just means
      // a shorter menu, never a broken one.
      const [pins, kpi] = await Promise.all([
        getDashboardPins(idToken).catch(() => [] as string[]),
        isAdmin
          ? listKpiDashboards(idToken, orgId)
              .then(kpiDashboardEntries)
              .catch(() => [] as DashboardEntry[])
          : Promise.resolve([] as DashboardEntry[]),
      ]);
      const next = { at: Date.now(), pins, kpi };
      cache.set(cacheKey, next);
      if (!cancelled) setData(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [session, orgId, cacheKey, isAdmin, tick]);

  return useMemo(() => {
    if (!ent) return null;
    const access: DashboardAccess = {
      isAdmin,
      entitlements: ent.entitlements,
      disabledFlags: new Set(flags.filter((f) => !f.enabled).map((f) => f.key)),
    };
    const visible = [...DASHBOARDS, ...(data?.kpi ?? [])].filter((e) => canSeeDashboard(e, access));
    const byKey = new Map(visible.map((e) => [e.key, e]));
    const pinned = (data?.pins ?? []).map((k) => byKey.get(k)).filter((e): e is DashboardEntry => !!e);
    const rest = visible.filter((e) => !pinned.includes(e) && !OWN_ROW.has(e.key));
    const crossApp = rest.filter((e) => e.kind === 'cross-app');
    const byApp = APP_ORDER.flatMap((app) => rest.filter((e) => e.kind === 'app' && e.app === app));
    const kpi = rest.filter((e) => e.kind === 'kpi');
    return [
      ...pinned.map(toItem(PINNED_GROUP)),
      ...crossApp.map(toItem('Across your apps')),
      ...byApp.map((e) => toItem(e.app ? APP_LABELS[e.app] : 'By app')(e)),
      ...kpi.map(toItem('Your KPI dashboards')),
      { to: '/dashboards', label: 'All dashboards', iconKey: 'dashboard', end: true, group: ' ' },
    ];
  }, [ent, flags, isAdmin, data]);
}
